"use client"
import styled from "@emotion/styled"
import { IconButton } from "@mui/material"
import { useState } from "react"
import "swiper/css"
import { Autoplay } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import {
  Container,
  Desktop,
  LeftArrow,
  Mobile,
  RightArrow,
  StartIcon,
  StopSign,
  transitionTime,
} from "ui"
import ImageWithAlt from "ui/ImageWithAlt"
import { sharedColors } from "ui/theme/colors"
import { extractImage } from "utils"
import { extractProperty } from "utils/extractProperty"

const StyledPaginatorContainer = styled.div`
  position: absolute;
  top: 19px;
  left: 16px;
  z-index: 10;
  background-color: white;
  padding: 10px;
  border-radius: 200px;

  ${(props) => props.theme.breakpoints.up("md")} {
    top: unset;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 3px 6px;
    border-radius: 200px;
  }
`

const StyledBulletsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin: 0 4px;
`
const StyledPlayButton = styled.div`
  display: flex;
  width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
  gap: 4.923px;
  border-radius: 123.077px;
  background-color: ${sharedColors.white};
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16);
  position: absolute;
  right: 25px;
  ${(props) => props.theme.breakpoints.up("lg")} {
    right: 10px;
  }
  top: 10px;

  cursor: pointer;
  z-index: 30;
`
const StyledBullet = styled.div<{ active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin: 0 2px;
  cursor: pointer;
  border: ${(props) =>
    props.active
      ? `1px solid ${props.theme.colors.propBlue}`
      : `1px solid ${props.theme.colors.black}`};
  background-color: ${(props) => (props.active ? props.theme.colors.propBlue : "none")};
`
const StyledBox = styled.div<{ galleryBottom?: boolean }>`
  ${(props) =>
    props.galleryBottom &&
    `
    ${props.theme.breakpoints.up("md")} {
      padding-bottom: 80px;
    }
    ${props.theme.breakpoints.down("md")} {
      padding-bottom: 40px;
    }
  `}
  .numbers-grid + & {
    padding-top: 32px;
    padding-bottom: 32px;
    ${(props) => props.theme.breakpoints.up("md")} {
      padding-top: 40px;
      padding-bottom: 40px;
    }
  }
`
type Props = {
  data: any
  locale: string
  galleryBottom?: boolean
}
const Slider = ({ data, locale, galleryBottom }: Props) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(true)
  const [swiperRef, setSwiperRef] = useState<any>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const slides = extractProperty(data, "slideElements").map((i: any) => {
    return {
      desktopMedia: extractImage(i, "imageOrVideoSelsctor"),
      mobileMedia: extractImage(i, "imageOrVideoSelectorMobaile"),
    }
  })

  const toggleAutoplay = () => {
    if (swiperRef) {
      if (isPlaying) {
        swiperRef.autoplay.stop()
      } else {
        swiperRef.autoplay.start()
      }
      setIsPlaying(!isPlaying)
    }
  }
  const desktopTrue = slides.some(
    (item: { desktopMedia: string | any[] }) => item.desktopMedia?.length > 0,
  )
  const mobileTrue = slides.some(
    (item: { mobileMedia: string | any[] }) => item.mobileMedia?.length > 0,
  )
  return (
    <StyledBox galleryBottom={galleryBottom}>
      <Container style={{ position: "relative" }}>
        <Swiper
          onSwiper={setSwiperRef}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex)
          }}
          style={{
            padding: 0,
            borderRadius: 16,
            position: "relative",
          }}
          modules={[Autoplay]}
          speed={transitionTime * 1000}
          autoplay={{
            // Configure autoplay options here
            delay: 4000, // Delay between transitions (in ms). For example, 2500ms here.
            disableOnInteraction: false, // Set to false if you want the autoplay to not stop on user interaction
          }}
        >
          {slides.map((item: any, key: number) => (
            <SwiperSlide
              key={key.toString()}
              style={{
                width: "100%",
              }}
            >
              <Desktop>
                {desktopTrue ? (
                  <ImageWithAlt
                    image={item?.desktopMedia}
                    height={560}
                    width={560}
                    style={{
                      height: "560px",
                      width: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : null}
              </Desktop>
              <Mobile>
                {mobileTrue ? (
                  <ImageWithAlt
                    image={item?.mobileMedia}
                    height={460}
                    width={560}
                    style={{
                      height: "460px",
                      width: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : null}
              </Mobile>
            </SwiperSlide>
          ))}
          <StyledPaginatorContainer>
            <Desktop>
              <IconButton
                onClick={() => {
                  swiperRef.slidePrev()
                }}
              >
                {locale === "en" ? <LeftArrow /> : <RightArrow />}
              </IconButton>
            </Desktop>
            <StyledBulletsContainer>
              {slides.map((_: any, index: number) => (
                <StyledBullet
                  key={index.toString()}
                  onClick={() => {
                    swiperRef.slideTo(index)
                  }}
                  active={index === activeIndex}
                />
              ))}
            </StyledBulletsContainer>
            <Desktop>
              <IconButton
                onClick={() => {
                  swiperRef.slideNext()
                }}
              >
                {locale === "en" ? <RightArrow /> : <LeftArrow />}
              </IconButton>
            </Desktop>
          </StyledPaginatorContainer>
        </Swiper>
        {desktopTrue ? (
          <Desktop>
            <StyledPlayButton
              onClick={toggleAutoplay}
              aria-label={isPlaying ? "stop-playing-button" : "start-playing-button"}
            >
              {isPlaying ? <StopSign /> : <StartIcon />}
            </StyledPlayButton>
          </Desktop>
        ) : null}
        {mobileTrue ? (
          <Mobile>
            <StyledPlayButton
              onClick={toggleAutoplay}
              aria-label={isPlaying ? "stop-playing-button" : "start-playing-button"}
            >
              {isPlaying ? <StopSign /> : <StartIcon />}
            </StyledPlayButton>
          </Mobile>
        ) : null}
      </Container>
    </StyledBox>
  )
}
export default Slider
